// ============================================================
// areaShapes (v8.364.4; the area block v8.369.0; v2 with dice sizes,
// the arc and the riders v8.372.1) - the template a weapon fires.
//
// ONE derivation for the map overlay, the victim selection, the attack's
// auto-hit and avoid test, the forecast and the notes. A weapon carries an
// AREA BLOCK typed on the sheet: a shape (cone, blast, beam, arc), its
// sizes, who it catches, the auto-hit and the avoid test, the riders that
// used to hang on special rules (cover bypass, scatter on a miss), and the
// arc's chain rules. Every size is a number OR a dice expression, rolled
// ONCE per attack through the engine dice (`resolveAreaSizes`) - the map
// and the forecast draw the EXPECTATION (`expectedArea`).
//
// A weapon without a block still derives one from the four legacy shape
// specials (Spray, Blast, Beam, Arcing), so a sheet saved before the block
// behaves exactly as it did; `upgradeAreaSpecials` writes that block down
// and strips the chips, once, when the weapon is loaded.
//
// Pure. Zero React dependency.
// ============================================================
import { hasWS, getWSP } from './weaponSpecials';
import { parseDiceExpression, rollDiceExpression, expectedDice } from './dice';
import { EFFECT_AFFECTS, type EffectAffects } from './effectScope';
import type { AfflictionTestId } from './afflictions';

/** Spray without a parameter: a hand flamer's cone rather than a zero-degree one that catches nobody. */
export const DEFAULT_SPRAY_DEGREES = 45;

/**
 * How far a cone reaches when the weapon does not say. A weapon saved
 * without a Range gave zero - a ONE METRE cone that caught nobody, the
 * target included. Twenty metres is a hand flamer's reach (2026-08-18).
 */
export const DEFAULT_SPRAY_LENGTH_M = 20;

/** A beam's full width when the block does not say: one metre each side, as the map always drew it. */
export const DEFAULT_BEAM_WIDTH_M = 2;

export type AreaShape = 'none' | 'cone' | 'blast' | 'beam' | 'arc';

/** A count in metres or degrees, or a dice expression ('1d5+2') rolled once per attack. */
export type AreaSize = number | string;

/** Who the template catches at all - by the attacker's team, strict (effectScope.ts). */
export type AreaCatches = EffectAffects;

/** The characteristic a victim tests to step out of the template; '' = no test, they are simply caught. */
export type AreaAvoid = '' | AfflictionTestId;

/** What a passed avoid test buys: clear of it, or the raw roll halved or quartered. */
export type AreaAvoidOutcome = 'avoided' | 'half' | 'quarter';

/**
 * How an arc's echoes lose damage, leap by leap, on the RAW roll before
 * armour: `percent` compounds (100 → 50 → 25 at 50), `flat` subtracts the
 * value each leap, `fixed` gives every echo the same share of its OWN
 * roll (every victim rolls fresh dice), `dice` rolls the echo's own expression instead of the weapon's.
 */
export type ArcFalloffMode = 'percent' | 'flat' | 'fixed' | 'dice';

interface ArcFalloff {
  mode: ArcFalloffMode;
  /** A percentage for percent/fixed, a number for flat, a dice expression for dice. */
  value: AreaSize;
  /** No echo deals less than this. */
  floor: number;
}

/**
 * The area block on a weapon. Flat on purpose: the editors patch it with a
 * spread, the adapters copy it with a spread, and Firestore stores it as it
 * is. Only the fields the shape uses matter; the rest keep their defaults.
 */
export interface WeaponArea {
  shape: AreaShape;
  /** A cone's included angle, in degrees. */
  angle: AreaSize;
  /** A cone's or a beam's reach in metres; 0 = the weapon's range, else a flamer's twenty. */
  length: AreaSize;
  /** A blast's radius in metres. */
  radius: AreaSize;
  /** A beam's full width in metres; 0 = two. */
  width: AreaSize;
  /** An arc's reach per leap, in metres. */
  reach: AreaSize;
  /** An arc's number of leaps - extra victims beyond the primary. */
  leaps: AreaSize;
  catches: AreaCatches;
  autoHit: boolean;
  avoid: AreaAvoid;
  /** ± on the victim's avoid test. */
  avoidMod: number;
  avoidOutcome: AreaAvoidOutcome;
  /** The template ignores cover AP (Spray's old rider). */
  coverBypass: boolean;
  /** A missed ranged shot scatters and lands anyway (Blast's old rider). */
  scatterOnMiss: boolean;
  falloff: ArcFalloff;
  /** Echoes may score criticals and Righteous Fury. */
  echoesCrit: boolean;
  /** Echoes get the reaction prompt like any victim of the volley. */
  echoesReact: boolean;
  /** Each leap needs line of sight from the last victim. */
  sightPerLeap: boolean;
  /** The SECONDARY template this one's victims spawn (v8.373.0); one level, never nested. */
  spawn?: SpawnedArea | null;
}

/** Which of the primary's victims spawn the secondary; `last` is an arc's final leap. */
export type SpawnedBy = 'every' | 'aimed' | 'last';

/**
 * The secondary template (v8.373.0): a block of its own, spawned AT a victim
 * of the primary. It has no attack roll, so no auto-hit and no scatter; its
 * falloff applies to every victim it catches, whatever its shape, and its
 * `echoes*` switches read as "spawned victims may crit / react".
 */
export type SpawnedArea = Omit<WeaponArea, 'autoHit' | 'scatterOnMiss' | 'spawn'> & { spawnedBy: SpawnedBy };

type Sized<T> = Omit<T, 'angle' | 'length' | 'radius' | 'width' | 'reach' | 'leaps' | 'falloff' | 'spawn'> & {
  angle: number;
  length: number;
  radius: number;
  width: number;
  reach: number;
  leaps: number;
  /** `dice` carries the echo's expression when the mode is dice, else ''. */
  falloff: { mode: ArcFalloffMode; value: number; floor: number; dice: string };
};

/** The secondary with every size a number - rolled once per shot, shared by every spawned copy. */
export type ResolvedSpawn = Sized<SpawnedArea>;

/** The block with every size a number, plus what was rolled to get there. */
export type ResolvedArea = Sized<WeaponArea> & {
  spawn?: ResolvedSpawn | null;
  /** `spawned` marks a roll made for the secondary template. */
  rolled: { field: string; expr: string; total: number; spawned?: boolean }[];
};

export const AREA_SPAWNED_BY: { id: SpawnedBy; label: string; hint: string }[] = [
  { id: 'every', label: 'Every victim', hint: 'Everyone the template catches spawns one.' },
  { id: 'aimed', label: 'The aimed target only', hint: 'Only the one you aimed at, if it was caught.' },
  { id: 'last', label: 'The last victim', hint: "The arc's final leap." },
];

export const AREA_SHAPES: { id: AreaShape; label: string }[] = [
  { id: 'none', label: 'None' },
  { id: 'cone', label: 'Cone' },
  { id: 'blast', label: 'Blast' },
  { id: 'beam', label: 'Beam' },
  { id: 'arc', label: 'Arc' },
];

export const AREA_CATCHES: { id: AreaCatches; label: string; hint: string }[] = EFFECT_AFFECTS.map((a) => ({
  id: a.key,
  label: a.key === 'anyone' ? 'Everyone' : a.label,
  hint: a.hint,
}));

export const AREA_AVOIDS: { id: AreaAvoid; label: string }[] = [
  { id: '', label: 'No test' },
  { id: 'agility', label: 'Agility' },
  { id: 'toughness', label: 'Toughness' },
  { id: 'strength', label: 'Strength' },
  { id: 'willpower', label: 'Willpower' },
];

export const AREA_AVOID_OUTCOMES: { id: AreaAvoidOutcome; label: string }[] = [
  { id: 'avoided', label: 'Steps clear' },
  { id: 'half', label: 'Half damage' },
  { id: 'quarter', label: 'A quarter' },
];

export const ARC_FALLOFF_MODES: { id: ArcFalloffMode; label: string; hint: string }[] = [
  { id: 'percent', label: 'Percent per leap', hint: 'Compounding: 100 → 50 → 25 at 50%.' },
  { id: 'flat', label: 'Flat drop per leap', hint: 'Subtract the value each leap: 16 → 12 → 8 at 4.' },
  { id: 'fixed', label: 'Fixed share', hint: 'Every echo takes the same percentage of its own roll.' },
  { id: 'dice', label: "The echo's own dice", hint: "Echoes roll this expression instead of the weapon's damage." },
];

export function emptyArea(): WeaponArea {
  return {
    shape: 'none',
    angle: DEFAULT_SPRAY_DEGREES,
    length: 0,
    radius: 0,
    width: 0,
    reach: 0,
    leaps: 0,
    catches: 'anyone',
    autoHit: false,
    avoid: '',
    avoidMod: 0,
    avoidOutcome: 'avoided',
    coverBypass: false,
    scatterOnMiss: false,
    falloff: { mode: 'percent', value: 50, floor: 0 },
    echoesCrit: false,
    echoesReact: true,
    sightPerLeap: true,
  };
}

/** A secondary with no shape yet: the primary's `catches`, spawned by every victim. */
export function emptySpawn(primary: Pick<WeaponArea, 'catches'>): SpawnedArea {
  const { autoHit: _autoHit, scatterOnMiss: _scatter, spawn: _spawn, ...rest } = emptyArea();
  return { ...rest, catches: primary.catches, spawnedBy: 'every' };
}

/** The four specials the block replaced (v8.372): still parsed off an old sheet, never offered. */
const LEGACY_SHAPE_SPECIALS = ['Spray', 'Blast', 'Beam', 'Arcing'] as const;

/** A block as a sheet may still hold it: any subset of the fields, and the v8.369 `size`. */
export type StoredArea = Partial<Omit<WeaponArea, 'spawn'>> & { size?: number; spawn?: Partial<SpawnedArea> | null };

interface AreaSource {
  area?: StoredArea | null;
  specialRules?: readonly string[] | null;
}

interface ReachSource extends AreaSource {
  coneLength?: number | null;
  range?: number | null;
}

/** A block that names a real shape - the one test every reader of a stored block makes. */
function hasShape<T extends { shape?: AreaShape | null }>(
  block: T | null | undefined,
): block is T & { shape: Exclude<AreaShape, 'none'> } {
  return !!block && !!block.shape && block.shape !== 'none';
}

/** Is this a size the block accepts - a non-negative number, or an expression the dice engine parses? */
export function isValidSize(v: AreaSize | null | undefined): boolean {
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0;
  if (typeof v !== 'string') return false;
  const t = v.trim();
  return t !== '' && (/^\d+$/.test(t) || parseDiceExpression(t).length > 0);
}

/** A size as a number: a number passes, a dice expression is rolled (or averaged) by `roll`; anything else is 0. */
export function sizeOf(v: AreaSize | null | undefined, roll: (expr: string) => number): number {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.max(0, v) : 0;
  if (typeof v !== 'string') return 0;
  const t = v.trim();
  if (/^\d+$/.test(t)) return parseInt(t, 10);
  return parseDiceExpression(t).length ? Math.max(0, roll(t)) : 0;
}

/** The expression behind a size, for the overlay's hint; null when it is a plain number. */
export function sizeExpression(v: AreaSize | null | undefined): string | null {
  return typeof v === 'string' && !/^\d*$/.test(v.trim()) ? v.trim() : null;
}

/**
 * A block as stored, brought to the current shape: a v8.369 `{ shape, size }`
 * block reads `size` as the cone's angle or the blast's radius, and any
 * field a save never carried takes the default. Never mutates its input.
 */
export function normaliseArea(block: StoredArea | null | undefined): WeaponArea {
  const base = emptyArea();
  if (!block) return base;
  const { size, spawn, ...rest } = block;
  const out: WeaponArea = { ...base, ...rest, falloff: { ...base.falloff, ...(block.falloff ?? {}) } };
  applyLegacySize(out, block, size);
  if (!out.shape) out.shape = 'none';
  // Set only when there is one: a stored block must never carry an `undefined` (gotcha #2).
  const secondary = normaliseSpawn(spawn, out);
  if (secondary) out.spawn = secondary;
  return out;
}

/** A v8.369 `size` is the cone's angle or the blast's radius - unless the block already types them. In place. */
function applyLegacySize(out: WeaponArea, block: StoredArea, size: number | undefined): void {
  if (!(typeof size === 'number' && size > 0) || block.angle !== undefined || block.radius !== undefined) return;
  if (out.shape === 'cone') out.angle = size;
  if (out.shape === 'blast') out.radius = size;
}

/** The stored secondary brought to shape: none without a shape on BOTH blocks, `last` only off an arc, never nested. */
function normaliseSpawn(stored: Partial<SpawnedArea> | null | undefined, primary: WeaponArea): SpawnedArea | null {
  if (!stored || !stored.shape || stored.shape === 'none' || primary.shape === 'none') return null;
  const base = emptySpawn(primary);
  const { spawn: _nested, autoHit: _autoHit, scatterOnMiss: _scatter, ...rest } = stored as Partial<WeaponArea>;
  const out: SpawnedArea = { ...base, ...rest, falloff: { ...base.falloff, ...(stored.falloff ?? {}) } };
  if (out.spawnedBy === 'last' && primary.shape !== 'arc') out.spawnedBy = 'every';
  if (!AREA_SPAWNED_BY.some((o) => o.id === out.spawnedBy)) out.spawnedBy = 'every';
  return out;
}

/**
 * The area the weapon's legacy SPECIALS imply - the preset each one stood
 * for. Spray(X): a cone of X° (45 by default) that auto-hits, is avoided by
 * Agility and ignores cover; Blast(X): a blast of X m that scatters on a
 * miss; Beam: a lance; Arcing(X): one leap of X m at half damage, crits and
 * fury stripped, no sight test - exactly what each rule always did.
 */
export function areaFromSpecials(specialRules: readonly string[] | null | undefined): WeaponArea {
  const sp = [...(specialRules || [])];
  const base = emptyArea();
  if (hasWS(sp, 'Spray')) return sprayPreset(base, getWSP(sp, 'Spray'));
  // Blast without a parameter (the old picker added `Blast(0)`) is the smallest blast, one metre.
  if (hasWS(sp, 'Blast')) {
    return { ...base, shape: 'blast', radius: Math.max(1, getWSP(sp, 'Blast') || 1), scatterOnMiss: true };
  }
  if (hasWS(sp, 'Beam')) return { ...base, shape: 'beam' };
  if (hasWS(sp, 'Arcing')) return arcingPreset(base, getWSP(sp, 'Arcing'));
  return base;
}

function sprayPreset(base: WeaponArea, degrees: number): WeaponArea {
  const angle = Math.min(360, Math.max(1, degrees || DEFAULT_SPRAY_DEGREES));
  return { ...base, shape: 'cone', angle, autoHit: true, avoid: 'agility', coverBypass: true };
}

function arcingPreset(base: WeaponArea, reach: number): WeaponArea {
  return {
    ...base,
    shape: 'arc',
    reach: Math.max(0, reach || 0),
    leaps: 1,
    falloff: { mode: 'percent', value: 50, floor: 0 },
    echoesCrit: false,
    echoesReact: true,
    sightPerLeap: false,
  };
}

/** The area the weapon takes: its block when it has a shape, else what its legacy specials imply. */
export function areaOf(slot: AreaSource | null | undefined): WeaponArea {
  const block = slot?.area;
  if (hasShape(block)) return normaliseArea(block);
  return areaFromSpecials(slot?.specialRules);
}

/**
 * Strip the four legacy shape chips off a weapon and write the block they
 * stood for, unless a block with a shape is already typed (it wins). The
 * one-shot upgrade the loaders run (v8.372.7): a weapon saved with
 * `Spray(30)` opens with a filled block and loses the chip on its next save.
 */
export function upgradeAreaSpecials(weapon: { specialRules?: readonly string[] | null; area?: StoredArea | null }): {
  specialRules: string[];
  area: WeaponArea | null;
  changed: boolean;
} {
  const rules = [...(weapon.specialRules ?? [])];
  const legacy = rules.filter((r) => LEGACY_SHAPE_SPECIALS.some((id) => hasWS([r], id)));
  if (!legacy.length)
    return { specialRules: rules, area: weapon.area ? normaliseArea(weapon.area) : null, changed: false };
  const typed = hasShape(weapon.area);
  return {
    specialRules: rules.filter((r) => !legacy.includes(r)),
    area: typed ? normaliseArea(weapon.area) : areaFromSpecials(rules),
    changed: true,
  };
}

type SizeField = 'angle' | 'length' | 'radius' | 'width' | 'reach' | 'leaps';

const SIZE_FIELDS: { field: SizeField; shapes: AreaShape[] }[] = [
  { field: 'angle', shapes: ['cone'] },
  { field: 'length', shapes: ['cone', 'beam'] },
  { field: 'radius', shapes: ['blast'] },
  { field: 'width', shapes: ['beam'] },
  { field: 'reach', shapes: ['arc'] },
  { field: 'leaps', shapes: ['arc'] },
];

type Take = (field: string, v: AreaSize) => number;

/**
 * One block's sizes, drawn through `take` in the FIXED order. `anyShapeFalloff`
 * is the secondary's rule: its falloff scales every victim it catches, so the
 * value is drawn whatever the shape; a primary's matters only to an arc's echoes.
 */
function sizesOf<T extends Omit<WeaponArea, 'autoHit' | 'scatterOnMiss' | 'spawn'>>(
  area: T,
  take: Take,
  anyShapeFalloff: boolean,
): Sized<T> {
  const sizes: Record<SizeField, number> = { angle: 0, length: 0, radius: 0, width: 0, reach: 0, leaps: 0 };
  // A FIXED ORDER, and only the fields the shape uses: the seeded dice
  // sequence depends on how many dice this draws (gotcha #5).
  for (const { field, shapes } of SIZE_FIELDS)
    sizes[field] = shapes.includes(area.shape) ? take(field, area[field]) : 0;
  const diceMode = area.falloff.mode === 'dice';
  const falloffValue = (anyShapeFalloff || area.shape === 'arc') && !diceMode ? take('falloff', area.falloff.value) : 0;
  const { spawn: _spawn, ...flat } = area as T & { spawn?: unknown };
  return {
    ...flat,
    ...sizes,
    falloff: {
      mode: area.falloff.mode,
      value: falloffValue,
      floor: Math.max(0, area.falloff.floor || 0),
      dice: diceMode ? String(area.falloff.value) : '',
    },
  } as Sized<T>;
}

/**
 * A `Take` that sizes through `roll`, recording each dice-valued size in
 * `rolled` when `record` is set (the log's clauses; the forecast skips it).
 */
function sizeTaker(roll: (expr: string) => number, rolled: ResolvedArea['rolled'] | null, spawned: boolean): Take {
  return (field, v) => {
    const expr = sizeExpression(v);
    const total = sizeOf(v, roll);
    if (expr && rolled) rolled.push({ field, expr, total, ...(spawned ? { spawned } : {}) });
    return total;
  };
}

function resolveWith(
  area: WeaponArea,
  specialRules: readonly string[] | null | undefined,
  roll: (expr: string) => number,
  record: boolean,
): ResolvedArea {
  const rolled: ResolvedArea['rolled'] = [];
  const log = record ? rolled : null;
  const primary = sizesOf(area, sizeTaker(roll, log, false), false);
  // Official Maximal: +2 to the blast radius when both are present (v8.197.8) - here, once, for every reader.
  if (area.shape === 'blast' && primary.radius > 0 && hasWS([...(specialRules || [])], 'Maximal')) primary.radius += 2;
  // THE SECONDARY ROLLS AFTER THE PRIMARY (v8.373.0), same order, once per
  // shot - a weapon without one draws exactly the dice it drew before.
  const spawn = hasShape(area.spawn) ? sizesOf(area.spawn, sizeTaker(roll, log, true), true) : null;
  return { ...primary, ...(spawn ? { spawn } : {}), rolled };
}

/** The block with every dice-valued size ROLLED, once, in a fixed order - the shot's actual template. */
export function resolveAreaSizes(
  area: WeaponArea,
  specialRules?: readonly string[] | null,
  roll: (expr: string) => number = (expr) => rollDiceExpression(expr).total,
): ResolvedArea {
  return resolveWith(area, specialRules, roll, true);
}

/** The block with every dice-valued size at its MEAN - what the overlay draws and the forecast reads. */
export function expectedArea(area: WeaponArea, specialRules?: readonly string[] | null): ResolvedArea {
  return resolveWith(area, specialRules, expectedDice, false);
}

/** One clause per rolled size, for the log: "radius 1d5+1 → 4 m"; the secondary's follow a "spawned:" label. */
export function describeRolledSizes(resolved: ResolvedArea): string {
  const unit = (field: string) => (field === 'angle' ? '°' : field === 'leaps' || field === 'falloff' ? '' : ' m');
  const clause = (r: ResolvedArea['rolled'][number]) => `${r.field} ${r.expr} → ${r.total}${unit(r.field)}`;
  const own = resolved.rolled.filter((r) => !r.spawned).map(clause);
  const spawned = resolved.rolled.filter((r) => r.spawned).map(clause);
  return [own.join(', '), spawned.length ? 'spawned: ' + spawned.join(', ') : ''].filter(Boolean).join('; ');
}

/**
 * The sentence the editors showed when a shape special disagreed with a
 * typed block (v8.371.0). With the chips gone from the picker it fires only
 * for a sheet not yet upgraded; kept until the loaders strip them.
 */
export function areaDisagreement(block: StoredArea | null | undefined, implied: WeaponArea): string | null {
  if (!hasShape(block) || implied.shape === 'none' || block.shape === implied.shape) return null;
  const label = (shape: AreaShape) => AREA_SHAPES.find((o) => o.id === shape)?.label ?? shape;
  return `${label(implied.shape)} on the specials, ${label(block.shape).toLowerCase()} on the block: the block wins.`;
}

/** The reach of a cone or a beam at its expectation: the block's length, else `coneLength`, else the Range, else a flamer's. */
export function sprayLengthOf(slot: ReachSource | null | undefined): number {
  const block = hasShape(slot?.area) ? expectedArea(normaliseArea(slot?.area)).length : 0;
  if (block > 0) return block;
  if (slot?.coneLength && slot.coneLength > 0) return slot.coneLength;
  const range = slot?.range || 0;
  return range > 0 ? range : DEFAULT_SPRAY_LENGTH_M;
}

function sourceOf(slot: AreaSource | readonly string[] | null | undefined): AreaSource | null | undefined {
  return Array.isArray(slot) ? { specialRules: slot as string[] } : (slot as AreaSource | null | undefined);
}

/** The cone's included angle at its expectation; 0 when the weapon has no cone. */
export function sprayAngleOf(slot: AreaSource | readonly string[] | null | undefined): number {
  const src = sourceOf(slot);
  const area = expectedArea(areaOf(src), src?.specialRules);
  return area.shape === 'cone' ? area.angle : 0;
}

/** The blast's radius at its expectation, Maximal included; 0 when the weapon has no blast. */
export function blastRadiusOf(slot: AreaSource | readonly string[] | null | undefined): number {
  const src = sourceOf(slot);
  const area = expectedArea(areaOf(src), src?.specialRules);
  return area.shape === 'blast' ? area.radius : 0;
}

/** Whether the weapon fires a beam - a lance from the muzzle. */
export function beamOf(slot: AreaSource | null | undefined): boolean {
  return areaOf(slot).shape === 'beam';
}

/** A beam's full width at its expectation, two metres when the block does not say. */
export function beamWidthOf(slot: AreaSource | null | undefined): number {
  const area = expectedArea(areaOf(slot), slot?.specialRules);
  return area.shape === 'beam' && area.width > 0 ? area.width : DEFAULT_BEAM_WIDTH_M;
}

/** What the avoid test is rolled against: the target's Agility, else the bonus × 10 statblock bases (the affliction's approximation). */
export function avoidBaseOf(
  target: {
    dodge?: { agility?: number } | null;
    toughnessBonus?: number;
    strengthBonus?: number | null;
    willpowerBonus?: number | null;
  },
  avoid: AfflictionTestId,
): number {
  if (avoid === 'agility') return target.dodge?.agility || 0;
  if (avoid === 'strength') return (target.strengthBonus ?? 3) * 10;
  if (avoid === 'willpower') return (target.willpowerBonus ?? 3) * 10;
  return (target.toughnessBonus ?? 0) * 10;
}

/** The characteristic's short name on the map's chip, as the sheet abbreviates it. */
const AVOID_SHORT: Record<AfflictionTestId, string> = { agility: 'AG', toughness: 'T', strength: 'S', willpower: 'WP' };
const AVOID_BUYS: Record<AreaAvoidOutcome, string> = { avoided: 'CLEAR', half: 'HALF', quarter: 'QUARTER' };

/**
 * A template's avoid test as the preview's chip under each victim's mark (plan design-gaps, T2; the areas page's
 * "Ag -> half"): the characteristic tested and what a pass buys; null when the template has no test.
 */
export function avoidChip(area: { avoid?: AreaAvoid; avoidOutcome?: AreaAvoidOutcome } | null | undefined) {
  if (!area?.avoid) return null;
  return `${AVOID_SHORT[area.avoid]} → ${AVOID_BUYS[area.avoidOutcome ?? 'half']}`;
}

/** The plain test a victim rolls to avoid the template - the affliction editor's vocabulary onto the target's bases. */
export const AVOID_TEST_LABEL: Record<AfflictionTestId, string> = {
  agility: 'Agility',
  toughness: 'Toughness',
  strength: 'Strength',
  willpower: 'Willpower',
};
